import { IconChevronRight } from '@tabler/icons-react'
import { useState } from 'react'

import { AstraCallout, AstraHoverCard, AstraIcon, AstraParagraph, getOnlyDate } from '~/astra'
import { useStore } from '~/store/store'
import { usePractitionerScheduleLocations, usePractitionerStatusDefinitions } from '~/storm'
import { sortStatusDefinitions } from '~/utils/surgeryActivities'

import { arrayHasAtLeastOneElement, DatePractitionerCell, NotePreview, useImportActivityLogs } from '../../shared'
import { PractitionerBottomBar } from '../../shared/BottomBar'
import { ActivityLog } from './ActivityLog/ActivityLog'
import { Header } from './Header'
import { LocationGroupElements } from './Locations/LocationGroupElements'
import { useLocationsForPractitionerScheduleModal } from './Locations/useLocationsForPractitionerScheduleModal'
import { ModalElement, ModalElementHeading } from './ModalElement'
import { PractitionerNote } from './PractitionerNote'
import { StatusDefinitionTags } from './Statuses/StatusDefinitionTags'

type LocationGroupKind = 'selected' | 'other'

function getLocationGroupLabel(kind: LocationGroupKind, isSpecialtiesSelectionEnabled: boolean, isMultipleCells: boolean) {
    const assignment = kind === 'other' ? 'ikke tildelt' : 'tildelt'
    const practitioners = isMultipleCells ? 'kirurgenes' : 'kirurgens'
    const scopeSingular = isSpecialtiesSelectionEnabled ? 'fagområde' : 'avdeling'
    const scopePlural = isSpecialtiesSelectionEnabled ? 'fagområder' : 'avdelinger'
    const scopeLabel = isMultipleCells ? scopePlural : scopeSingular

    return `Stuer ${assignment} ${practitioners} ${scopeLabel}`
}

type Props = {
    selectedCells: DatePractitionerCell[]
}

export const PractitionerScheduleModal = ({ selectedCells }: Props) => {
    const departmentKey = useStore(state => state.appFilters.departmentKey)

    const { locationGroups, isSpecialtiesSelectionEnabled } = useLocationsForPractitionerScheduleModal(selectedCells)
    const statusDefinitions = usePractitionerStatusDefinitions({ include: { departments: true } })
    const orderedPractitionerStatusDefinitions = sortStatusDefinitions(statusDefinitions)

    const allPractitionerScheduleLocationsByCell = usePractitionerScheduleLocations({
        where: {
            OR: selectedCells.map(({ date, practitionerId }) => ({ scheduled_date: getOnlyDate(date), practitioner_id: practitionerId })),
        },
    })
    const firstCell = selectedCells.at(0)
    const singleCell = selectedCells.length === 1
    const isMultipleCells = selectedCells.length > 1

    const selectedSpecialtiesLocations = locationGroups.selectedSpecialties
    const otherSpecialtiesLocations = locationGroups.otherSpecialties

    const [showNote, setShowNote] = useState(false)
    const [showActivityLog, setShowActivityLog] = useState(false)

    const { isLoading } = useImportActivityLogs(firstCell)

    if (showNote && firstCell && singleCell) return <PractitionerNote cell={firstCell} hideNote={() => setShowNote(false)} />
    if (showActivityLog && firstCell && singleCell) return <ActivityLog cell={firstCell} hideLog={() => setShowActivityLog(false)} isLoading={isLoading} />

    const selectedLocationGroupLabel = getLocationGroupLabel('selected', isSpecialtiesSelectionEnabled, isMultipleCells)
    const otherLocationGroupLabel = getLocationGroupLabel('other', isSpecialtiesSelectionEnabled, isMultipleCells)

    return (
        <div className="pointer-events-none relative flex w-72 flex-col pt-3" tabIndex={0}>
            <Header selectedCells={selectedCells} />

            {isMultipleCells && (
                <div className="border-b p-3">
                    <AstraCallout color="info" text="Du ser kun aktiviteter og stuer som kan settes på hele utvalget ditt." />
                </div>
            )}

            <div className="border-b">
                <ModalElement label="Aktivitet">
                    <StatusDefinitionTags
                        statusDefinitions={orderedPractitionerStatusDefinitions}
                        selectedCells={selectedCells}
                        departmentKey={departmentKey}
                    />
                </ModalElement>
            </div>

            <div className="border-b">
                <ModalElementHeading className="px-3 pb-1 pt-2">{selectedLocationGroupLabel}</ModalElementHeading>
                <LocationGroupElements
                    group={selectedSpecialtiesLocations}
                    selectedCells={selectedCells}
                    practitionerScheduleLocations={allPractitionerScheduleLocationsByCell}
                />

                <AstraHoverCard
                    placement="right-start"
                    openDelay={0}
                    closeDelay={0}
                    showArrow={false}
                    sideOffset={0}
                    alignOffset={-16}
                    noPadding
                    hoverContent={
                        <div className="flex max-h-96 w-72 flex-col gap-1 overflow-y-auto py-2">
                            <ModalElementHeading className="border-b border-base px-3 pb-2 text-subtle">{otherLocationGroupLabel}</ModalElementHeading>
                            <LocationGroupElements
                                group={otherSpecialtiesLocations}
                                selectedCells={selectedCells}
                                practitionerScheduleLocations={allPractitionerScheduleLocationsByCell}
                            />
                        </div>
                    }
                >
                    <div className="pointer-events-auto mb-3 flex items-center justify-between px-2 py-2 hover:bg-surface-hover">
                        <AstraParagraph variant="body-small" className="text-subtle">
                            {otherLocationGroupLabel}
                        </AstraParagraph>
                        <div className="flex items-center gap-2">
                            <AstraParagraph variant="body-small" className="text-subtle">
                                {otherSpecialtiesLocations.maxCount}
                            </AstraParagraph>
                            <AstraIcon icon={IconChevronRight} />
                        </div>
                    </div>
                </AstraHoverCard>
            </div>

            {firstCell && singleCell && <NotePreview cell={firstCell} openNote={() => setShowNote(true)} />}

            {arrayHasAtLeastOneElement(selectedCells) && (
                <PractitionerBottomBar selectedCells={selectedCells} openNote={() => setShowNote(true)} openActivityLog={() => setShowActivityLog(true)} />
            )}
        </div>
    )
}
